import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import { useTranslation } from 'react-i18next';
import CustomDropdown from './CustomDropdown';
import { useContainerScale } from './hooks/useViewportScale';
import { API_URL } from './config/apiConfig';
import { POST_LOGIN_VIDEO_PATH, POST_LOGIN_VIDEO_SRC } from './PostLoginVideo';
import { preloadVideo } from './hooks/mediaPreload';
import { isSteamClient } from './utils/isSteamClient';
import './Auth.css';

function SteamLoginPage({ setUser }) {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [error, setError] = useState('');
  const [status, setStatus] = useState('signingIn');
  const [currentLanguage, setCurrentLanguage] = useState(i18n.language || 'en-us');

  const {
    scale: viewportScale,
    offsetX: viewportOffsetX,
    offsetY: viewportOffsetY,
    baseWidth: containerBaseWidth,
    baseHeight: containerBaseHeight,
    isMobile: isContainerMobile,
  } = useContainerScale();

  useEffect(() => {
    if (!isSteamClient()) {
      throw new Error('SteamLoginPage can only render in the Steam client build');
    }
    if (!window.electron || !window.electron.steam) {
      throw new Error('Steam IPC is required. Launch CODA from Steam.');
    }

    let cancelled = false;

    async function signInWithSteam() {
      try {
        setStatus('signingIn');
        setError('');
        const ticketResult = await window.electron.steam.getSessionTicket();
        if (!ticketResult || !ticketResult.steamTicket) {
          throw new Error('Steam session ticket is required');
        }
        const response = await fetch(`${API_URL}/auth/steam`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ steamTicket: ticketResult.steamTicket }),
        });
        let data;
        try {
          data = await response.json();
        } catch (parseErr) {
          throw new Error(`Steam login response was not JSON: ${parseErr.message}`);
        }
        if (!response.ok) {
          throw new Error(data.error || data.message || t('login.steamErrorFailed'));
        }
        if (!data.token || !data.refreshToken || !data.user || !data.user.username) {
          throw new Error('Steam login did not return token, refreshToken, and user');
        }
        const decoded = jwtDecode(data.token);
        if (!decoded.userId) {
          throw new Error('Steam login token is missing userId');
        }
        if (cancelled) {
          return;
        }
        localStorage.setItem('token', data.token);
        localStorage.setItem('refreshToken', data.refreshToken);
        localStorage.setItem('user', data.user.username);
        localStorage.setItem('userId', String(decoded.userId));
        setUser({ username: data.user.username, userId: decoded.userId });
        navigate(POST_LOGIN_VIDEO_PATH);
      } catch (err) {
        console.error('Steam login failed:', err);
        if (cancelled) {
          return;
        }
        setStatus('error');
        const isNetwork =
          typeof err?.message === 'string' && err.message.includes('Failed to fetch');
        if (isNetwork) {
          setError(`${t('login.errorConnectionFailure')} ${t('login.errorNetworkHint')}`);
        } else {
          setError(err?.message || t('login.steamErrorFailed'));
        }
      }
    }

    signInWithSteam();
    return () => {
      cancelled = true;
    };
  }, [navigate, setUser]);

  useEffect(() => {
    preloadVideo(POST_LOGIN_VIDEO_SRC).catch((err) => {
      console.warn('Post-login video preload failed (non-critical):', err?.message || err);
    });
  }, []);

  const handleLanguageChange = (event) => {
    const next = event.target.value;
    setCurrentLanguage(next);
    localStorage.setItem('i18nextLng', next);
    const normalized = next === 'en-us' ? 'en-US' : next === 'de-de' ? 'de-DE' : next === 'es-es' ? 'es-ES' : next;
    i18n.changeLanguage(normalized);
  };

  const retry = () => {
    setError('');
    setStatus('signingIn');
    window.location.reload();
  };

  return (
    <div className="auth-viewport-wrapper">
      <div className="auth-fullscreen-background" />
      <div
        className="auth-viewport-container"
        style={{
          width: `${containerBaseWidth}px`,
          height: `${containerBaseHeight}px`,
          transformOrigin: '0 0',
          transform: `translate(${viewportOffsetX}px, ${viewportOffsetY}px) scale(${viewportScale})`,
        }}
      >
        <div className={`auth-container ${isContainerMobile ? 'auth-mobile' : ''}`}>
          <div className="auth-card">
            <img
              src="/images/logo.png"
              alt="Chronicles of the Dying: Anthology"
              className="auth-logo"
            />
            {status === 'signingIn' && !error && (
              <p className="auth-form">{t('login.steamSigningIn')}</p>
            )}
            {error && (
              <div className="auth-error">
                {error}
                <div style={{ marginTop: 12 }}>
                  <button type="button" className="auth-button" onClick={retry}>
                    {t('login.steamRetry')}
                  </button>
                </div>
              </div>
            )}
            <div className="auth-language-selector-inline">
              <CustomDropdown
                options={[
                  { value: 'en-us', label: t('common.languageEnglish'), flags: ['gb', 'us', 'au'] },
                  { value: 'de-de', label: t('common.languageGerman'), flags: ['de', 'at', 'ch'] },
                  { value: 'es-es', label: t('common.languageSpanish'), flags: ['es', 'co', 'ar'] },
                ]}
                value={currentLanguage}
                onChange={(value) => {
                  handleLanguageChange({ target: { value } });
                }}
                placeholder={t('common.languageEnglish')}
                openUpward={isContainerMobile}
                renderOption={(option) => (
                  <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <span style={{ display: 'flex', gap: '4px' }}>
                      {option.flags && option.flags.map((flag, idx) => (
                        <span
                          key={idx}
                          className={`fi fi-${flag}`}
                          style={{ fontSize: isContainerMobile ? '0.9em' : '1.1em' }}
                        />
                      ))}
                    </span>
                    <span>{option.label}</span>
                  </span>
                )}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SteamLoginPage;
